import React from "react";
import styled from "styled-components";
import { Unplug } from "lucide-react";
import { BasicBotData, BotData, LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useBotStatusTag } from "@shared/features/bots/hooks";
import { AccountObjectType } from "@shared/features/settings/types";
import { hexToRGB } from "@shared/utils/misc";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";

interface NewLookBotStatusProps {
  bot: BotData | BasicBotData;
}

/**
 * New look: the bot's status as a quiet dot + word (no tinted pill). Same
 * colours, copy and invalid-account tooltip as the classic status tag; the
 * word stays muted while the bot is running normally.
 * Meant for a container-query card (see NewLookBotCard).
 */
const NewLookBotStatus = ({ bot }: NewLookBotStatusProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { color, invalidAccountData, isBotActive, isBotArchived } =
    useBotStatusTag(bot);
  const hasInvalidAccount = invalidAccountData.isInvalidOrInactive;
  const text = isBotArchived
    ? t("filters.status.common.archived")
    : isBotActive
    ? t("filters.status.common.active")
    : t("filters.status.common.inactive");
  const isCalm = isBotActive && !hasInvalidAccount;

  return (
    <InactiveAccountIcon
      objectType={AccountObjectType.BOT}
      accountId={bot.client_id}
      invalidColor={color}
      hideIcon={true}
    >
      <Status
        $color={color}
        $isCalm={isCalm}
        title={hasInvalidAccount ? undefined : text}
      >
        {hasInvalidAccount ? (
          <Unplug size={13} strokeWidth={2} color={color} />
        ) : (
          <Dot $color={color} />
        )}
        <Text>{text}</Text>
      </Status>
    </InactiveAccountIcon>
  );
};

export default React.memo(NewLookBotStatus);

const Status = styled.span<{ $color: string; $isCalm: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 20px;
  ${newLookType.label};
  line-height: 20px;
  color: ${(p) => (p.$isCalm ? getNewLookTokens(p.theme).secondary : p.$color)};
  white-space: nowrap;
`;
/** On very narrow cards (small phones) only the dot stays, like classic. */
const Text = styled.span`
  @container (max-width: 279px) {
    display: none;
  }
`;
const Dot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  flex: none;
  border-radius: 50%;
  background: ${(p) => p.$color};
  box-shadow: 0 0 0 3px ${(p) => hexToRGB(p.$color, 0.16)};
`;
